import { PaymentMethodType } from '@entities/payment-methods/model'
import type { PaymentType } from '@server/schemas'
import { match, P } from 'ts-pattern'
import { z } from 'zod'

import { PaymentCustomData } from './portone.model'

type Payment = {
  paymentType: PaymentType
  paymentMethod: Nullable<PaymentMethodType>
  originalPrice: number
  lessonMonth: number
  discountedPrice: Undefinable<number>
  originalPriceOverride?: number
  totalPriceOverride?: number
}

/**
 * Custom type to replace iamport-typings RequestPayParams
 * Only defines fields actually used by our payment params
 */
interface BaseRequestPayParams {
  pg?: string
  pay_method?: string
  merchant_uid: string
  name?: string
  amount: number
  buyer_email?: string
  buyer_name?: string
  buyer_tel?: string
  buyer_addr?: string
  buyer_postcode?: string
  m_redirect_url?: string
  notice_url?: string
}

export type RequestTossPaymentParams = BaseRequestPayParams & {
  channelKey: string
  custom_data: PaymentCustomData
}
export type RequestKakaoPayParams = BaseRequestPayParams & {
  channelKey: string
  custom_data: PaymentCustomData
}
export type RequestNaverPayParams = BaseRequestPayParams & {
  channelKey: string
  custom_data: PaymentCustomData
  naverUseCfm: string
  naverPopupMode: boolean
  naverProducts: {
    categoryType: 'PRODUCT'
    categoryId: 'DIGITAL_CONTENT'
    uid: string
    name: string
    count: number
  }[]
}

export type PaymentSlackMessageBody = {
  action: 'SLACK_FAIL' | 'SLACK_TRIAL_FAIL' | 'SLACK_DELIVERY'
  contents: {
    message: string
  }
}

// V1/V2 호환성을 위한 refine 함수
const v1V2CompatibleRefine = <
  T extends { imp_uid?: string; merchant_uid?: string; payment_id?: string; paymentId?: string },
>(
  data: T,
) => (data.imp_uid && data.merchant_uid) || data.payment_id || data.paymentId

const v1V2CompatibleRefineMessage = 'Either V1 params (imp_uid, merchant_uid) or V2 params (payment_id) required'

// Billing 결제(FIRST_BILLING, TRIAL)용 V1/V2 호환 refine
// billingKey 또는 issue_id가 있으면 유효한 빌링키 발급 리다이렉트로 인정
const billingV1V2CompatibleRefine = <
  T extends {
    imp_uid?: string
    merchant_uid?: string
    payment_id?: string
    paymentId?: string
    billingKey?: string
    issue_id?: string
  },
>(
  data: T,
) => (data.imp_uid && data.merchant_uid) || data.payment_id || data.paymentId || data.billingKey || data.issue_id

const billingV1V2CompatibleRefineMessage =
  'V1 params (imp_uid, merchant_uid), V2 params (payment_id/paymentId), or billing params (billingKey/issue_id) required'

// Base 스키마 (extend 및 .shape 접근 가능)
export const basePaymentRedirectedSearchParamsSchema = z.object({
  purchase_order_id: z.string().optional(),
  paymentToken: z.string().optional(),
  payment_token: z.string().optional(),
  txId: z.string().optional(),
  code: z.string().optional(),
  imp_success: z.string().optional(),
  product_id: z.string().optional(),
  // V1 파라미터 (optional로 변경 - V2 호환)
  imp_uid: z.string().optional(),
  merchant_uid: z.string().optional(),
  // V2 파라미터 추가
  payment_id: z.string().optional(), // V2 paymentId (snake_case 호환)
  paymentId: z.string().optional(), // V2 SDK가 리다이렉트 시 camelCase로 전달
  tx_id: z.string().optional(), // V2 txId
  complete: z.literal('true'),
  amount: z.string().transform((val) => Number(val)),
  pg_provider: z.enum(['KAKAO_PAY', 'TOSS_PAYMENTS', 'NAVER_PAY']),
})

// export용 스키마 (refine 적용) - 직접 사용시 검증
export const commonPaymentRedirectedSearchParamsSchema = basePaymentRedirectedSearchParamsSchema.refine(
  v1V2CompatibleRefine,
  { message: v1V2CompatibleRefineMessage },
)

export const trialPaymentRedirectedSearchParamsSchema = basePaymentRedirectedSearchParamsSchema
  .extend({
    payment_type: z.literal('TRIAL'),
    issue_id: z.string().optional(), // 앱 환경 리다이렉트 후 billingKey 조회용
    customer_uid: z.string().optional(), // 웹 환경 하위 호환성
    billingKey: z.string().optional(), // 포트원 V2 SDK가 리다이렉트 시 직접 전달하는 billingKey
    trial_level: z.enum(['B', 'C1', 'C2', 'D']),
    subscribe_id: z.string(),
  })
  .refine(billingV1V2CompatibleRefine, { message: billingV1V2CompatibleRefineMessage })
  .refine((data) => data.issue_id || data.customer_uid || data.billingKey, {
    message: 'PAYMENT_BILLING_KEY_REQUIRED',
  })

export const firstBillingPaymentRedirectedSearchParamsSchema = basePaymentRedirectedSearchParamsSchema
  .extend({
    payment_type: z.literal('FIRST_BILLING'),
    issue_id: z.string().optional(), // 앱 환경 리다이렉트 후 billingKey 조회용
    customer_uid: z.string().optional(), // 웹 환경 하위 호환성
    billingKey: z.string().optional(), // 포트원 V2 SDK가 리다이렉트 시 직접 전달하는 billingKey
    coupon_id: z.string().optional(),
  })
  .refine(billingV1V2CompatibleRefine, { message: billingV1V2CompatibleRefineMessage })
  .refine((data) => data.issue_id || data.customer_uid || data.billingKey, {
    message: 'PAYMENT_BILLING_KEY_REQUIRED',
  })

export const lumpSumPaymentRedirectedSearchParamsSchema = basePaymentRedirectedSearchParamsSchema
  .extend({
    payment_type: z.enum(['LUMP_SUM', 'IPAD']),
    coupon_id: z.string().optional(),
    zip_code: z.string().optional(),
    address: z.string().optional(),
    detail_address: z.string().optional(),
  })
  .refine(v1V2CompatibleRefine, { message: v1V2CompatibleRefineMessage })

export type TrialPaymentRedirectedSearchParamsSchema = z.infer<typeof trialPaymentRedirectedSearchParamsSchema>
export type FirstBillingPaymentRedirectedSearchParamsSchema = z.infer<
  typeof firstBillingPaymentRedirectedSearchParamsSchema
>
export type LumpSumPaymentRedirectedSearchParamsSchema = z.infer<typeof lumpSumPaymentRedirectedSearchParamsSchema>

class PaymentModel {
  constructor(private readonly payment: Payment) {}

  static create(payment: Payment) {
    return new PaymentModel(payment)
  }

  get isDeliveryPayment(): boolean {
    return match(this.payment.paymentType)
      .with(P.union('IPAD'), () => true)
      .otherwise(() => false)
  }

  get originalPrice(): number {
    if (this.payment.originalPriceOverride !== undefined) {
      return this.payment.originalPriceOverride
    }

    if (this.payment.paymentType === 'IPAD' || this.payment.paymentType === 'LUMP_SUM') {
      return this.payment.originalPrice * this.installmentPeriod
    }

    return this.payment.originalPrice
  }

  get totalPrice(): number {
    if (this.payment.totalPriceOverride !== undefined) {
      return this.payment.totalPriceOverride
    }

    return this.originalPrice - this.discountPrice
  }

  get installmentPeriod(): number {
    return this.payment.lessonMonth
  }

  get installmentPrice(): number {
    return this.truncateDecimalPoint(this.totalPrice / this.installmentPeriod)
  }

  get discountPrice(): number {
    if (this.payment.totalPriceOverride !== undefined) {
      return Math.max(0, this.originalPrice - this.payment.totalPriceOverride)
    }

    if (!this.payment.discountedPrice) {
      return 0
    }

    return this.truncateDecimalPoint(this.payment.discountedPrice)
  }

  get isShowConfirmDialogBeforePayment(): boolean {
    return !(
      this.payment.paymentMethod === 'KAKAO_PAY' ||
      this.payment.paymentMethod === 'REGISTERED_KAKAO_PAY' ||
      this.payment.paymentMethod === 'NAVER_PAY' ||
      this.payment.paymentMethod === 'REGISTERED_NAVER_PAY' ||
      this.payment.paymentType === 'LUMP_SUM' ||
      this.payment.paymentType === 'IPAD'
    )
  }

  private truncateDecimalPoint(amount: number): number {
    return Math.floor(amount)
  }
}

export default PaymentModel
